import React from 'react';
import { X, Calendar, Upload, Download } from 'lucide-react';

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const BandwidthUsageModal = ({ isOpen, onClose, bandwidthUsage, onUpgrade }) => {
  if (!isOpen || !bandwidthUsage) return null;

  const usagePercentage = Math.round((bandwidthUsage.totalBytes / bandwidthUsage.limitBytes) * 100);
  const isNearLimit = usagePercentage >= 80;
  const isOverLimit = usagePercentage >= 100;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1d29',
        border: '1px solid #374151',
        borderRadius: '8px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        position: 'relative',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <X style={{ height: '20px', width: '20px' }} />
        </button>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Calendar style={{ height: '24px', width: '24px', color: '#3b82f6' }} />
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#e2e8f0', margin: 0 }}>
              Monthly Bandwidth Usage
            </h2>
          </div>
          <p style={{ fontSize: '14px', color: '#9ca3af', margin: 0 }}>
            Track your monthly data usage (by IP, UTC month)
          </p>
        </div>

        {/* Current Month and IP */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px',
          padding: '12px',
          backgroundColor: '#242938',
          borderRadius: '6px',
          border: '1px solid #374151'
        }}>
          <div>
            <span style={{ fontSize: '14px', color: '#d1d5db' }}>Current Month: </span>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0' }}>
              {new Date().toISOString().slice(0, 7)}
            </span>
          </div>
          <div>
            <span style={{ fontSize: '14px', color: '#d1d5db' }}>IP: </span>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0' }}>
              {bandwidthUsage.ip || 'Unknown'}
            </span>
          </div>
        </div>

        {/* Usage Summary */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '12px' 
          }}>
            <span style={{ fontSize: '16px', fontWeight: '500', color: '#e2e8f0' }}>
              {formatBytes(bandwidthUsage.totalBytes)} / {formatBytes(bandwidthUsage.limitBytes)}
            </span>
            <span style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: isOverLimit ? '#ef4444' : isNearLimit ? '#f59e0b' : '#10b981'
            }}>
              ({usagePercentage}%)
            </span>
          </div>

          {/* Progress Bar */}
          <div style={{ 
            width: '100%', 
            height: '12px', 
            backgroundColor: '#374151', 
            borderRadius: '6px',
            overflow: 'hidden',
            marginBottom: '12px'
          }}>
            <div style={{ 
              width: `${Math.min(usagePercentage, 100)}%`, 
              height: '100%', 
              backgroundColor: isOverLimit ? '#ef4444' : isNearLimit ? '#f59e0b' : '#3b82f6',
              borderRadius: '6px',
              transition: 'width 0.3s ease'
            }}></div>
          </div>

          {/* Upload/Download Breakdown */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            fontSize: '14px',
            color: '#9ca3af'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Upload style={{ height: '16px', width: '16px', color: '#3b82f6' }} />
              <span>Upload: {formatBytes(bandwidthUsage.uploadBytes)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Download style={{ height: '16px', width: '16px', color: '#10b981' }} />
              <span>Download: {formatBytes(bandwidthUsage.downloadBytes)}</span>
            </div>
          </div>
        </div>

        {/* Warning Messages */}
        {isOverLimit && (
          <div style={{ 
            backgroundColor: '#ef4444', 
            color: 'white', 
            padding: '12px', 
            borderRadius: '6px', 
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div style={{ fontSize: '16px' }}>⚠️</div>
            <div>
              <strong>Bandwidth limit exceeded!</strong><br />
              You've reached your monthly limit. Upgrade to continue downloading and uploading.
            </div>
          </div>
        )}

        {isNearLimit && !isOverLimit && (
          <div style={{ 
            backgroundColor: '#f59e0b', 
            color: 'white', 
            padding: '12px', 
            borderRadius: '6px', 
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div style={{ fontSize: '16px' }}>⚠️</div>
            <div>
              <strong>Approaching bandwidth limit</strong><br />
              You're using {usagePercentage}% of your monthly allowance. Consider upgrading for unlimited bandwidth.
            </div>
          </div>
        )}

        {/* Reset Info */}
        <div style={{ 
          textAlign: 'center', 
          fontSize: '12px', 
          color: '#9ca3af',
          padding: '12px',
          backgroundColor: '#242938',
          borderRadius: '6px',
          border: '1px solid #374151'
        }}>
          <div>Resets on the 1st of each month (UTC)</div>
          <div style={{ marginTop: '4px' }}>
            Next reset: {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString()}
          </div>
        </div>

        {isOverLimit && (
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
            <button onClick={onUpgrade} style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              padding: '10px 16px',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: 'pointer'
            }}>Upgrade Plan</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BandwidthUsageModal;




